import { mappedColors } from '@pixee/design-system'
import type { RunTool } from './exploit-chains-run.ts'

/**
 * Colors and names shared by the correlation canvas, the hypothesis log and the run summary, so
 * a tool is the same hue wherever it appears.
 *
 * Tool hues are assigned in the fixed order the run reports tools (largest contributor first) and
 * never cycled: a seventh tool folds into the last hue rather than inventing one.
 */

export const TOOL_COLOR_TOKENS = [
  mappedColors.primary.ion,
  mappedColors.primary.solar,
  mappedColors.secondary.wave,
  mappedColors.primary.glow,
  mappedColors.secondary.halcyon,
  mappedColors.secondary.umbra,
] as const

export const HYPOTHESIS_COLOR_TOKEN = mappedColors.primary.pulse
export const HELD_COLOR_TOKEN = mappedColors.primary.nebula
export const GHOST_COLOR_TOKEN = mappedColors.neutral.neutral50
// Deliberately outside TOOL_COLOR_TOKENS so an errored chord never shares a hue with a tool.
export const ERRORED_COLOR_TOKEN = mappedColors.secondary.ember
export const NEVER_INDEXED_COLOR_TOKEN = mappedColors.neutral.neutral40

export const toolColorToken = (index: number): string =>
  TOOL_COLOR_TOKENS[Math.min(index, TOOL_COLOR_TOKENS.length - 1)] ?? TOOL_COLOR_TOKENS[0]

const TOOL_DISPLAY_NAMES: Record<string, string> = {
  semgrep: 'Semgrep',
  zap: 'ZAP',
  osv: 'OSV',
  codeql: 'CodeQL',
  gitleaks: 'Gitleaks',
  sonar: 'Sonar',
  checkmarx: 'Checkmarx',
  snyk: 'Snyk',
  trufflehog: 'TruffleHog',
  appscan: 'AppScan',
  unknown: 'Unknown tool',
}

export const toolDisplayName = (tool: string): string =>
  TOOL_DISPLAY_NAMES[tool.toLowerCase()] ?? tool.charAt(0).toUpperCase() + tool.slice(1)

const KIND_LABELS: Record<string, string> = {
  sast: 'SAST',
  dast: 'DAST',
  sca: 'SCA',
  secrets: 'Secrets',
  unknown: '',
}

export const kindLabel = (kind: string): string => KIND_LABELS[kind] ?? kind.toUpperCase()

/** The tool a finding id belongs to, by position in the run's tool list. */
export const toolIndexOfFinding = (tools: readonly RunTool[], findingId: string): number =>
  tools.findIndex(tool => tool.findingIds.includes(findingId))

/**
 * Resolves a design-system color token to a literal the Canvas 2D API accepts.
 *
 * Tokens are `var(--name)` references into the active theme; canvas `fillStyle` does not
 * understand them, so the variable is read off the element's computed style at draw time. That
 * keeps the canvas on the same theme as the DOM around it without hard-coding a single hex.
 */
export const resolveCssColor = (token: string, element: Element): string => {
  const variable = /^var\((--[^,)]+)/.exec(token)?.[1]
  if (!variable) {
    return token
  }
  const resolved = getComputedStyle(element).getPropertyValue(variable).trim()
  return resolved || token
}
